import {
  CirclesThreeIcon,
  cn,
  customTv,
  FilesIcon,
  GitLogo,
  StackletLogo,
  Tooltip,
  UsersThreeIcon,
} from "@stacklet/ui";

import { Link } from "app/components/links";

const rbacChipStyles = customTv({
  base: "flex h-auto w-fit items-center justify-center gap-xs rounded-1 border border-legacy-navy-L70 bg-transparent px-xxs py-3xs",
  variants: {
    variant: {
      column:
        "max-w-[14.0625rem] text-label-extra-small-strong text-legacy-neutrals-ash",
      role: "min-w-auto text-label-small-strong text-legacy-neutrals-eerieBlack",
      // Rounded pill used in the role assignment table scope column: neutral
      // fill, no border, icon + visible label. It hugs its label and only
      // ellipsizes when the container itself is too narrow (a table cell), so
      // a name with room to spare is shown in full; the full value is on hover.
      scope:
        "min-w-0 max-w-full rounded-full border-0 bg-background-secondary pl-xs pr-lg text-label-medium text-text-primary",
    },
  },
  defaultVariants: {
    variant: "column",
  },
});

type RbacChipKey =
  | "column-system"
  | "column-policy-collection"
  | "column-account-group"
  | "column-user-group"
  | "column-repository";

const rbacChipVariantMap: Record<
  RbacChipKey,
  { icon: React.ReactNode; label?: string }
> = {
  "column-system": {
    icon: <StackletLogo className="size-3.5 shrink-0" />,
    label: "System",
  },
  "column-policy-collection": {
    icon: <FilesIcon className="size-3.5 shrink-0" />,
  },
  "column-account-group": {
    icon: <CirclesThreeIcon className="size-3.5 shrink-0" />,
  },
  "column-user-group": {
    icon: <UsersThreeIcon className="size-3.5 shrink-0" />,
  },
  "column-repository": {
    icon: <GitLogo className="size-3.5 shrink-0" />,
  },
};

export const typenameChipMap: Record<string, RbacChipKey> = {
  UserGroup: "column-user-group",
  AccountGroup: "column-account-group",
  System: "column-system",
  RoleScope: "column-system",
  PolicyCollection: "column-policy-collection",
  RepositoryConfig: "column-repository",
  Repository: "column-repository",
};

const rbacChipTooltipMap: Record<RbacChipKey, string> = {
  "column-system": "System",
  "column-policy-collection": "Policy Collection",
  "column-account-group": "Account Group",
  "column-user-group": "Group",
  "column-repository": "Repository",
};

function linkTooltipTitle(rbacType: RbacChipKey, label: React.ReactNode) {
  const type = rbacChipTooltipMap[rbacType].toLowerCase();
  return typeof label === "string" && label ? (
    <>
      Click to open {type}{" "}
      <strong className="text-label-small-strong">{label}</strong>
    </>
  ) : (
    `Click to open ${type}`
  );
}

interface RbacChipProps {
  rbacType?: RbacChipKey;
  variant?: "column" | "role" | "scope";
  label?: React.ReactNode;
  className?: string;
  /**
   * Internal route for the chip's details page. When set, the whole pill
   * becomes a link. Omit for a target with nowhere to go (the System scope, or
   * a resource the viewer can't open).
   */
  to?: string;
}

export function RbacChip({
  rbacType,
  variant,
  label,
  className,
  to,
}: RbacChipProps) {
  if (rbacType) {
    const chip = rbacChipVariantMap[rbacType];
    if (!chip) return null;

    const displayLabel = label ?? chip.label ?? "";
    const chipContent = (
      <span
        className={rbacChipStyles({
          variant: variant ?? "column",
          className: cn(
            to ? "transition-colors hover:bg-background-tertiary" : undefined,
            className,
          ),
        })}
      >
        {chip.icon}
        {displayLabel ? (
          <span
            className={variant === "scope" ? "min-w-0 truncate" : undefined}
          >
            {displayLabel}
          </span>
        ) : null}
      </span>
    );

    if (to) {
      // The link wraps the pill so the whole chip is the hit target. The
      // tooltip goes inside it (`asChild`, so no nested button) and the visible
      // label supplies the link's accessible name.
      return (
        <Link
          className="inline-flex min-w-0 max-w-full"
          textDecoration="none"
          to={to}
        >
          <Tooltip
            className="break-all"
            title={linkTooltipTitle(rbacType, displayLabel)}
            asChild
          >
            {chipContent}
          </Tooltip>
        </Link>
      );
    }

    if (variant === "scope") {
      // The pill ellipsizes a long label, so put the full value in a hover
      // tooltip rather than the redundant type tooltip the other variants use.
      return typeof displayLabel === "string" && displayLabel ? (
        <Tooltip
          buttonProps={{
            "aria-label": displayLabel,
            className:
              "inline-flex min-w-0 max-w-full cursor-default border-none bg-transparent p-0 outline-none",
          }}
          className="break-all"
          title={displayLabel}
        >
          {chipContent}
        </Tooltip>
      ) : (
        chipContent
      );
    }

    return (
      <Tooltip
        buttonProps={{
          "aria-label": rbacChipTooltipMap[rbacType],
          className:
            "inline-flex cursor-default border-none bg-transparent p-0 outline-none",
        }}
        title={rbacChipTooltipMap[rbacType]}
      >
        {chipContent}
      </Tooltip>
    );
  }

  return (
    <span className={rbacChipStyles({ variant, className })}>{label}</span>
  );
}
